<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      persistent
      width="1024"
      @click:outside="dialog = false"
    >
      <template #activator="{ props }">
        <v-btn class="text-capitalize text-grey" variant="text" v-bind="props">
          + Adicionar Bug
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <div class="d-flex justify-space-between">
            <span class="text-h5"
              >Adicionar Bug para Area de Concentração {{ index + 1 }}</span
            >
            <v-btn
              variant="plain"
              rounded="xl"
              prepend-icon="mdi-window-close"
              @click="dialog = false"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-form ref="form" validate-on="lazy submit">
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="descricao"
                    required
                    label="Descrição*"
                    placeholder="Descrição do bug encontrado"
                    rows="3"
                    :rules="descricaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="observacao"
                    required
                    label="Observação*"
                    placeholder="Informações adicionais sobre o bug"
                    rows="3"
                    :rules="observacaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="tipo_bug"
                    :items="['CORRECAO', 'MELHORIA']"
                    label="Tipo*"
                    required
                    :rules="tipoBugRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="gravidade"
                    :items="[
                      'BLOQUEADORA',
                      'CRITICA',
                      'MEDIA',
                      'BAIXA',
                      'TRIVIAL',
                    ]"
                    label="Gravidade*"
                    required
                    :rules="gravidadeRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-file-input
                    accept="image/*"
                    v-model="imagens"
                    multiple
                    label="Insira Imagens, Aceita apenas .png, .jpg e .jpeg"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-file-input
                    accept="video/*"
                    v-model="videos"
                    multiple
                    label="Insira Videos, Aceita apenas .mp4 e .mkv"
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*Campo obrigatório</small>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="blue-darken-1" variant="text" @click="validaEEnvia">
            Salvar
            <v-progress-circular
              v-if="this.isLoading"
              class="ml-2"
              indeterminate
              size="20"
            />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
/**
 * @module AreaConcentracaoCriarBug
 * @desc Modal com formulário para a criação de um bug dentro da
 * área de concentração.
 * @vue-data {boolean} [dialog=false] - Flag que indica se o modal está visível.
 * @vue-data {string} [userID=''] - O id do usuário atualmente logado.
 * @vue-data {string} [descricao=''] - Descrição do bug.
 * @vue-data {string} [observacao=''] - Observação sobre o bug.
 * @vue-data {string} [tipo_bug=''] - Tipo do bug.
 * @vue-data {string} [gravidade=''] - Gravidade do bug.
 * @vue-data {string} [csuId=''] - O id do caso de uso relacionado à área de
 * concentração.
 * @vue-data {string} idProjeto - O id do projeto.
 * @vue-data {Array.<String>} [imagens=[]] - Array com os caminhos para
 * as imagens.
 * @vue-data {Array.<String>} [videos=[]] - Array com os caminhos para
 * os vídeos.
 * @vue-data {boolean} [isLoading=false] - Fllag que indica se o bug ainda
 * está sendo criado.
 */
import axios from 'axios';
import FormData from 'form-data';

import store from '@/store/store';
const apiUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    id: {
      type: Number,
      required: true,
    },
    index: Number,
    csu: Object,
  },
  emits: ['bugCriado'],
  data: () => ({
    dialog: false,
    userID: '',
    descricao: '',
    observacao: '',
    // eslint-disable-next-line camelcase
    tipo_bug: '',
    gravidade: '',
    csuId: '',
    idProjeto: null,
    imagens: [],
    videos: [],
    isLoading: false,
    descricaoRules: [(v) => !!v || 'Descrição é obrigatória'],
    observacaoRules: [
      (v) => !!v || 'Observação é obrigatória',
      (v) =>
        (v && v.length <= 100) ||
        'Observação deve ter no máximo 500 caracteres',
    ],
    tipoBugRules: [(v) => !!v || 'Tipo do bug é obrigatório'],
    gravidadeRules: [(v) => !!v || 'Gravidade é obrigatória'],
  }),
  created() {
    this.idProjeto = this.$route.params.idProjeto;
    this.userID = localStorage.getItem('idUsuario');
    this.getCsu();
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Realiza uma requisição HTTP para obter os casos de uso do projeto.
     */
    getCsu() {
      axios
        .get(`${apiUrl}/api/v1/areaConcentracao/${this.id}/caso-de-uso/`, {
          headers: {
            Authorization: 'Bearer ' + store.state.token,
          },
        })
        .then((response) => {
          this.csuId = Number(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    /**
     * Realiza requisição HTTP para criar um novo bug na área de concentração.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        const data = {
          // eslint-disable-next-line camelcase
          id_caso_de_uso: this.csuId,
          descricao: this.descricao,
          observacao: this.observacao,
          // eslint-disable-next-line camelcase
          tipo_bug: this.tipo_bug,
          // eslint-disable-next-line camelcase
          gravidade: this.gravidade,
          // eslint-disable-next-line camelcase
          id_area_concentracao: Number(this.id),
          casosDeUsos: this.csuIds,
          // eslint-disable-next-line camelcase
          id_usuario: Number(this.userID),
        };

        try {
          this.isLoading = true;
          const formDataImagem = new FormData();
          const formDataVideo = new FormData();

          if (this.imagens.length > 0) {
            for (const imagem of this.imagens) {
              formDataImagem.append('files', imagem);
            }
          }
          if (this.videos.length > 0) {
            for (const video of this.videos) {
              formDataVideo.append('files', video);
            }
          }

          const config = {
            headers: {
              Authorization: `Bearer ${store.state.token}`,
            },
          };
          const response = await axios.post(
            `${apiUrl}/api/v1/bug/areaConcentracao/${this.id}`,
            data,
            config,
          );

          if (this.imagens.length > 0) {
            await axios({
              method: 'post',
              url: `${apiUrl}/api/v1/bug/arquivo/${response.data.idBug}`,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              data: formDataImagem,
            });
          }

          if (this.videos.length > 0) {
            await axios({
              method: 'post',
              url: `${apiUrl}/api/v1/bug/arquivo/${response.data.idBug}`,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              data: formDataVideo,
            });
          }

          this.$toast.success(response.data.msg);
          this.$emit('bugCriado', this.id);
        } catch (error) {
          console.log(error);
        } finally {
          this.isLoading = false;
          this.dialog = false;
          this.limpaFormulario();
        }
      }
    },
    /**
     * Método para validar as imagens e videos
     */

    validaEEnvia() {
      var nenhumErro = true;
      console.log(this.imagens);
      if (this.imagens.length > 0) {
        for (const imagem of this.imagens) {
          if (imagem.type != 'image/jpeg' && imagem.type != 'image/png') {
            this.$toast.error('Formato incorreto da(s) imagem.');
            nenhumErro = false;
            break;
          }
        }
      }
      if (this.videos.length > 0) {
        for (const video of this.videos) {
          if (video.type != 'video/mp4') {
            this.$toast.error('Formato incorreto do(s) video.');
            nenhumErro = false;
            break;
          }
        }
      }
      if (nenhumErro) {
        this.submitForm();
      }
    },

    /**
     * Método auxiliar para limpar os campos do formulário após o seu envio.
     */
    limpaFormulario() {
      this.descricao = '';
      this.observacao = '';
      // eslint-disable-next-line camelcase
      this.tipo_bug = '';
      this.gravidade = '';
    },
  },
};
</script>
